import type { RecordApiKind, RecordByKind } from '@fruuit/shared'
import { Button, Callout, Dialog, DialogCancel, useToast } from '@fruuit/ui'
import { type ReactNode, useId, useMemo, useState } from 'react'
import { useApiMutation } from '../../api/hooks'
import { recordsApi, useInvalidateRecords } from './api'
import {
  createPayload,
  type FieldErrors,
  type FieldSpec,
  FormAlert,
  type FormValues,
  initialValues,
  RecordFields,
  serverErrors,
  updatePayload,
  validateRecord,
} from './form'

export interface RecordFormDialogProps<K extends RecordApiKind> {
  kind: K
  open: boolean
  onClose: () => void
  title: string
  description?: ReactNode
  fields: readonly FieldSpec[]
  /** Edit this record (PATCH with its revision); omit to create. */
  record?: RecordByKind[K] | null
  /** Pre-filled values for a create. */
  defaults?: FormValues
  /** Values sent with a create but not shown (e.g. the parent `engagement_id`). */
  fixed?: FormValues
  /** Fields shown read-only when editing. */
  locked?: readonly string[]
  intro?: ReactNode
  submitLabel?: string
  /** FORM-026: say what did and did not happen. */
  successMessage?: string
  onSaved?: (record: RecordByKind[K]) => void
  size?: 'sm' | 'md' | 'lg'
}

/**
 * The shared create/edit dialog behind every authoring form (P-UI-001, FORM-001..028):
 * single-flight submit with a stable request id, shared-schema validation before sending,
 * server field errors inline, and a 409 kept inside the open dialog.
 */
export function RecordFormDialog<K extends RecordApiKind>(props: RecordFormDialogProps<K>) {
  if (!props.open) return null
  return <RecordFormDialogBody {...props} />
}

function RecordFormDialogBody<K extends RecordApiKind>({
  kind,
  onClose,
  title,
  description,
  fields,
  record,
  defaults,
  fixed,
  locked,
  intro,
  submitLabel,
  successMessage,
  onSaved,
  size = 'lg',
}: RecordFormDialogProps<K>) {
  const formId = useId()
  const { toast } = useToast()
  const invalidate = useInvalidateRecords()
  const editing = Boolean(record)
  const initial = useMemo(
    () => initialValues(fields, record as Record<string, unknown> | null | undefined, defaults),
    [fields, record, defaults],
  )
  const [values, setValues] = useState<FormValues>(initial)
  const [errors, setErrors] = useState<FieldErrors>({})
  const [formError, setFormError] = useState<string | null>(null)

  const mutation = useApiMutation(
    (body: FormValues, ctx) =>
      record ? recordsApi.update(kind, record.id, body, ctx) : recordsApi.create(kind, body, ctx),
    {
      onSuccess: async (saved) => {
        await invalidate()
        toast({
          tone: 'success',
          message:
            successMessage ??
            (editing ? 'Changes saved.' : 'Record created. Nothing is visible to clients yet.'),
        })
        onSaved?.(saved as RecordByKind[K])
        onClose()
      },
      onError: (error) => {
        const parsed = serverErrors(error)
        setErrors(parsed.fields)
        setFormError(parsed.form)
      },
    },
  )

  function change(name: string, value: unknown) {
    setValues((current) => ({ ...current, [name]: value }))
    if (errors[name]) setErrors(({ [name]: _removed, ...rest }) => rest)
  }

  function submit() {
    const full = { ...fixed, ...createPayload(fields, values) }
    const check = validateRecord(
      kind,
      full,
      fields.map((field) => field.name),
    )
    setErrors(check.fields)
    setFormError(check.form)
    if (Object.keys(check.fields).length > 0 || check.form) return
    if (record) {
      const changes = updatePayload(fields, values, initial)
      for (const name of locked ?? []) delete changes[name]
      if (Object.keys(changes).length === 0) {
        setFormError('There are no changes to save.')
        return
      }
      mutation.mutate({ revision: record.revision, ...changes })
    } else {
      mutation.mutate(full)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      description={description}
      size={size}
      footer={
        <>
          <DialogCancel />
          <Button type="submit" form={formId} loading={mutation.isPending}>
            {submitLabel ?? (editing ? 'Save changes' : 'Create')}
          </Button>
        </>
      }
    >
      <form
        id={formId}
        noValidate
        onSubmit={(event) => {
          event.preventDefault()
          submit()
        }}
      >
        <FormAlert message={formError} />
        {intro ? (
          <Callout tone="note" className="mb-5">
            {intro}
          </Callout>
        ) : null}
        <RecordFields
          kind={kind}
          fields={fields}
          values={values}
          errors={errors}
          onChange={change}
          disabled={mutation.isPending}
          locked={editing ? locked : undefined}
        />
      </form>
    </Dialog>
  )
}
